import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Chip,
  Divider,
  Avatar,
  LinearProgress,
  useTheme,
  Card,
  CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import WebIcon from '@mui/icons-material/Web';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import TerminalIcon from '@mui/icons-material/Terminal';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  position: 'relative',
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: theme.palette.divider,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  marginBottom: theme.spacing(1),
  fontWeight: 700,
  letterSpacing: '-0.5px',
  color: theme.palette.text.primary,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 40,
    height: 3,
    backgroundColor: theme.palette.primary.main,
  }
}));

const SkillChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(100, 181, 246, 0.1)' 
    : 'rgba(46, 80, 119, 0.08)',
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.75rem',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(100, 181, 246, 0.2)' 
      : 'rgba(46, 80, 119, 0.15)',
  },
}));

const SkillCard = styled(Card)(({ theme }) => ({
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[4],
  },
}));

const CategoryIcon = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  backgroundColor: theme.palette.primary.main + '15',
  color: theme.palette.primary.main,
  marginRight: theme.spacing(1.5),
}));

const SkillBar = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const SkillBarLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(0.5),
}));

const CustomLinearProgress = styled(LinearProgress)(({ theme, barcolor }) => ({
  height: 6,
  borderRadius: 3,
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.08)' 
    : 'rgba(0, 0, 0, 0.05)',
  '& .MuiLinearProgress-bar': {
    borderRadius: 3,
    backgroundColor: barcolor || theme.palette.primary.main,
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const SkillsList = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(1),
}));

const Skills = () => {
  const theme = useTheme();
  
  const skillCategories = [
    {
      title: "Programming Languages",
      icon: <TerminalIcon />,
      skills: [
        { name: "Java", level: 99 },
        { name: "SQL", level: 90 },
        { name: "Python", level: 85 },
      ]
    },
    {
      title: "Backend Development",
      icon: <CodeIcon />,
      skills: [
        { name: "Spring Boot", level: 90 },
        { name: "RESTful APIs", level: 90 },
        { name: "Microservices", level: 90 },
      ]
    },
    // {
    //   title: "Database & Storage",
    //   icon: <StorageIcon />,
    //   skills: [
    //     { name: "PostgreSQL", level: 90 },
    //     { name: "Redis", level: 80 },
    //     { name: "Elasticsearch", level: 80 },
    //   ]
    // },
    {
      title: "Architecture & Tools",
      icon: <ArchitectureIcon />,
      skills: [
        { name: "System Design", level: 90 },
        { name: "Distributed Systems", level: 85 },
        { name: "Performance Optimization", level: 92 },
      ]
    }
  ];

  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemAnimation = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const getProgressColor = (level) => {
    if (level >= 90) return theme.palette.success.main;
    if (level >= 80) return theme.palette.primary.main;
    if (level >= 70) return theme.palette.secondary.main;
    return theme.palette.warning.main;
  };

  return (
    <SectionContainer id="skills">
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <SectionTitle variant="h2" gutterBottom sx={{ mb: 1, '&::after': { left: 'calc(50% - 20px)' } }}>
            Skills & Expertise
          </SectionTitle>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              maxWidth: '700px', 
              mx: 'auto',
              fontSize: '1rem'
            }}
          >
            My technical skillset and areas of expertise developed over years of professional experience.
          </Typography>
        </Box>

        <motion.div
          variants={containerAnimation}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={3}>
            {skillCategories.map((category, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <motion.div variants={itemAnimation}>
                  <SkillCard>
                    <CardContent sx={{ p: 3 }}>
                      <CardHeader>
                        <CategoryIcon>
                          {category.icon}
                        </CategoryIcon>
                        <Typography variant="h6" fontWeight={600}>
                          {category.title}
                        </Typography>
                      </CardHeader>
                      
                      <Divider sx={{ mt: 1, mb: 2 }} />
                      
                      <SkillsList>
                        {category.skills.map((skill, idx) => (
                          <SkillBar key={idx}>
                            <SkillBarLabel>
                              <Typography variant="body2" fontWeight={500}>
                                {skill.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {skill.level}%
                              </Typography>
                            </SkillBarLabel>
                            <CustomLinearProgress 
                              variant="determinate" 
                              value={skill.level} 
                              barcolor={getProgressColor(skill.level)}
                            />
                          </SkillBar>
                        ))}
                      </SkillsList>
                    </CardContent>
                  </SkillCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Additional Technologies & Tools
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mt: 2, 
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {["Spring Boot", "Spring Security", "SOLID Principles", "SQL","Git", "Jenkins", "Grafana", "Prometheus", "Apache Kafka", "RabbitMQ", "Nginx", "S3", "EC2", "Serverless", "Docker", "Kubernetes", "Maven", "Gradle", "JUnit", "Mockito", "Linux", "Bash"].map((item) => (
                <SkillChip key={item} label={item} />
              ))}
            </Box>
          </Paper>
        </Box>
      </Container>
    </SectionContainer>
  );
};

export default Skills;